import React, { useContext, useEffect, useState, useRef } from "react";
import { IProps } from "./types";
import { IColorStop, IShapeRectangleAttributes } from "../../../types";
import RectangleCanvasElement from "./rectangle";
import CircleCanvasElement from "./circle";
import StarCanvasElement from "./star";
import EllipseCanvasElement from "./ellipse";
import LineCanvasElement from "./line";
import CustomCanvasElement from "./custom";
import TriangleCanvasElement from "./triangle";
import RhombusCanvasElement from "./rhombus";
import Konva from "konva";
import { IResizeValues } from "../../CommonCommandsOptions/ResizeCommandOption/types";
import { getMaskAndClipping, getBlendMode, setMasks } from "@utils/render";
import { storageAccountName, containerName } from "@storage/azure-storage-blob";
import { IShapeCommonAttributes, IShapeCustomAttributes } from "../../../types";
import { defineZIndex } from "@utils/zIndexChains";
import { KonvaEventObject } from "konva/lib/Node";
import { Transformer } from "react-konva";
import CornersAddIn from "../AddIns/CornersAddIn";
import useImage from "use-image";
import ElementLockedAddIn from "../AddIns/ElementLockedAddIn";
import { updateSelectorIcon } from "@utils/selector";
import { getCornerRadius } from "@utils/geometry";
import colors from "@assets/themes/colors";

const useCanvas = (props: IProps) => {
  const attributes = props.element.attributes as IShapeCommonAttributes;
  const urlImage = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${
    props.folder
  }/${attributes.srcImage ?? props.element.id}.png`;
  const [image] = useImage(urlImage, "anonymous");

  const [maskImages, setMaskImages] = useState<
    (HTMLImageElement | undefined)[]
  >([]);
  const [canvas, setCanvas] = React.useState<HTMLCanvasElement>();

  useEffect(() => {
    if (attributes.rawProps)
      setMasks(attributes.rawProps, props.folder, setMaskImages);
  }, []);

  useEffect(() => {
    if (!image || attributes.shapeType !== "Custom") {
      return;
    }
    const canvasElement = document.createElement("canvas");
    let ctx = canvasElement.getContext("2d");

    if (ctx) {
      const width = attributes.width;
      const height = attributes.height;
      canvasElement.width = width! + (attributes.borderWidth ?? 0);
      canvasElement.height = height + (attributes.borderWidth ?? 0);
      if (attributes.backgroundColor || attributes.bgGradient) {
        const customAttributes = props.element
          .attributes as IShapeCustomAttributes;
        canvasElement.width =
          customAttributes.initWidth + (attributes.borderWidth ?? 0);
        canvasElement.height =
          customAttributes.initHeight + (attributes.borderWidth ?? 0);
        ctx.beginPath();

        let borderFactor = (attributes.borderWidth ?? 0) / 2;

        for (var i = 0; i <= customAttributes.knots.length; i++) {
          let knot =
            customAttributes.knots[i < customAttributes.knots.length ? i : 0];
          let next = i < customAttributes.knots.length ? i + 1 : 1;
          if (next >= customAttributes.knots.length) next = 0;
          let nextknot = customAttributes.knots[next];
          //if (i == 0) ctx.moveTo(knot.points[2] - customAttributes.initX + (attributes.borderWidth??0), knot.points[3] - customAttributes.initY+(attributes.borderWidth??0));
          if (i == 0)
            ctx.moveTo(
              knot.points[2] - customAttributes.initX + borderFactor,
              knot.points[3] - customAttributes.initY + borderFactor
            );

          ctx.bezierCurveTo(
            knot.points[4] - customAttributes.initX + borderFactor,
            knot.points[5] - customAttributes.initY + borderFactor,
            nextknot.points[2] - customAttributes.initX + borderFactor,
            nextknot.points[3] - customAttributes.initY + borderFactor,
            nextknot.points[2] - customAttributes.initX + borderFactor,
            nextknot.points[3] - customAttributes.initY + borderFactor
          );

          if ((attributes.borderWidth ?? 0) > 0 && attributes.borderColor) {
            ctx.lineWidth = attributes.borderWidth ?? 0;
            ctx.setLineDash([
              attributes.borderDash ?? 0,
              attributes.borderWhiteSpace ?? 0,
            ]);
            ctx.strokeStyle = attributes.borderColor;
            ctx.stroke();
          }
        }
        ctx.closePath();

        ctx.fillStyle = getBgColor(attributes, ctx);
        ctx.fill();
      } else {
        ctx.drawImage(image, 0, 0);
      }

      getMaskAndClipping(ctx, maskImages, width!, height, attributes);
      setCanvas(canvasElement);
    }
  }, [
    image,
    maskImages,
    attributes.backgroundColor,
    attributes.bgGradient,
    attributes.borderColor,
    attributes.borderWhiteSpace,
    attributes.borderWidth,
    attributes.borderDash,
  ]);

  return canvas;
};

const getBgColor = (attributes: IShapeCommonAttributes, ctx?: any) => {
  if (attributes.bgGradient && attributes.bgGradient.hasGradient) {
    if (!ctx) {
      const canvas = document.createElement("canvas");
      ctx = canvas.getContext("2d");
    }
    if (ctx) {
      const angle = ((90 + (attributes.bgGradient.angle ?? 0)) / 180) * Math.PI;
      const length =
        Math.abs(attributes.width! * Math.sin(angle)) +
        Math.abs(attributes.height * Math.cos(angle));
      const halfx = (Math.sin(angle) * length) / 2.0;
      const halfy = (Math.cos(angle) * length) / 2.0;
      const cx = attributes.width! / 2.0;
      const cy = attributes.height / 2.0;
      const x1 = cx - halfx;
      const y1 = cy - halfy;
      const x2 = cx + halfx;
      const y2 = cy + halfy;

      let offsetX = cx;
      let offsetY = cy;

      let hex: any = "0x" + attributes.backgroundColor?.replace("#", "");
      let r = (hex >> 16) & 0xff;
      let g = (hex >> 8) & 0xff;
      let b = hex & 0xff;

      var gr: any;
      if (attributes.bgGradient.style == "linear")
        gr = ctx.createLinearGradient(x1, y1, x2, y2);
      else if (attributes.bgGradient.style == "radial") {
        if (attributes.bgGradient.offset) {
          if (attributes.bgGradient.offset.x !== 0)
            offsetX += attributes.width! * attributes.bgGradient.offset.x;
          if (attributes.bgGradient.offset.y !== 0)
            offsetY += attributes.height! * attributes.bgGradient.offset.y;
        }

        let ratio = cx * (attributes.bgGradient.scale ?? 1);
        gr = ctx.createRadialGradient(
          offsetX,
          offsetY,
          0,
          offsetX,
          offsetY,
          ratio
        );
      }
      if (attributes.bgGradient.colorStops && gr) {
        attributes.bgGradient.colorStops.map((colorstop: IColorStop, index) => {
          let hexColor: any = "0x" + colorstop.color?.replace("#", "");
          let rC = (hexColor >> 16) & 0xff;
          let gC = (hexColor >> 8) & 0xff;
          let bC = hexColor & 0xff;
          if (hexColor)
            gr.addColorStop(
              colorstop.location,
              "rgba(" +
                rC +
                "," +
                gC +
                "," +
                bC +
                "," +
                (colorstop.opacity ?? 0) +
                ")"
            );
        });
      }

      return gr;
    }
  } else return attributes.backgroundColor;
};

const ShapeCanvasElement: React.FC<IProps> = (props) => {
  const [image, setImage] = React.useState<HTMLImageElement>();

  let attributes = props.element.attributes as IShapeRectangleAttributes;
  const shapeRef = useRef<Konva.Shape>();
  const trRef = useRef() as React.MutableRefObject<Konva.Transformer>;
  const lockIconRef = React.useRef() as React.MutableRefObject<Konva.Image>;
  const canvas = useCanvas({ ...props });
  const node = shapeRef.current;

  const showDescribedColor: boolean =
    props.describedLayerColor !== "" && !attributes.hidden;

  useEffect(() => {
    if (canvas) {
      let newImage = new Image();
      newImage.src = canvas.toDataURL();
      newImage.onload = () => {
        setImage(newImage);
      };
    }
  }, [canvas]);

  useEffect(() => {
    if (node && (props.isSelected || props.isOver || showDescribedColor)) {
      trRef.current.nodes([node]);
      trRef.current.getLayer()?.batchDraw();
      props.showTransfomerOutside(trRef, shapeRef, props.isSelected);
    }
  }, [props.isSelected, props.isOver, props.describedLayerColor, props.zoom]);

  useEffect(() => {
    if (lockIconRef.current) lockIconRef.current.visible(false);
    if (
      props.element?.attributes &&
      (props.isSelected || props.isOver || showDescribedColor)
    )
      updateSelectorIcon(
        trRef.current,
        lockIconRef.current,
        attributes.locked === true &&
          !props.isSelectDescribe &&
          !props.isMultipleSelection
      );
  }, [
    trRef.current,
    lockIconRef.current,
    props.isOver,
    attributes.locked,
    props.isSelectDescribe,
    props.isMultipleSelection,
  ]);

  useEffect(() => {
    defineZIndex(shapeRef, props.element.id, props.zIndexChain);
  }, [JSON.stringify(props.zIndexChain)]);


    const getTransformerBorderColor = () => {
      if (props.isMultipleSelection && props.isOver) return colors.ORANGE;
      else
      return  showDescribedColor && !(props.isSelected || props.isOver) ? props.describedLayerColor: undefined
    };
    
  const getStroke = () => {
    if (attributes.shapeType !== "Custom") {
      if (
        attributes.shapeType === "Line" &&
        attributes.bgGradient &&
        attributes.bgGradient.hasGradient
      )
        return getBgColor(attributes);
      else return attributes.borderColor ?? "black";
    }
    return undefined;
  };

  let x1 = 0;
  let y1 = 0;
  let x2 = 0;
  let y2 = 0;
  let r = 0;
  let g = 0;
  let b = 0;
  let offsetX = 0;
  let offsetY = 0;
  let ratio = 0;

  let colorStops: (string | number)[] = [];

  if (node && attributes.bgGradient && attributes.bgGradient.hasGradient) {
    const angle = ((90 + (attributes.bgGradient.angle ?? 0)) / 180) * Math.PI;
    const length =
      Math.abs(node.width() * Math.sin(angle)) +
      Math.abs(node.height() * Math.cos(angle));
    const halfx = (Math.sin(angle) * length) / 2.0;
    const halfy = (Math.cos(angle) * length) / 2.0;

    const cx = node.width() / 2.0;
    const cy = node.height() / 2.0;
    offsetX = cx;
    offsetY = cy;

    if (attributes.bgGradient.offset) {
      if (attributes.bgGradient.offset.x !== 0)
        offsetX += node.width() * attributes.bgGradient.offset.x;
      if (attributes.bgGradient.offset.y !== 0)
        offsetY += node.height() * attributes.bgGradient.offset.y;
    }

    ratio = cx * (attributes.bgGradient.scale ?? 1);
    x1 = cx - halfx;
    y1 = cy - halfy;
    x2 = cx + halfx;
    y2 = cy + halfy;
    if (attributes.shapeType != "Rectangle") {
      x1 = x1 - cx;
      y1 = y1 - cy;
      x2 = x2 - cx;
      y2 = y2 - cy;
    }

    let hex: any = "0x" + attributes.backgroundColor?.replace("#", "");
    r = (hex >> 16) & 0xff;
    g = (hex >> 8) & 0xff;
    b = hex & 0xff;

    if (attributes.bgGradient.colorStops) {
      attributes.bgGradient.colorStops.map((colorstop: IColorStop, index) => {
        colorStops.push(colorstop.location);
        let hexColor: any = "0x" + colorstop.color?.replace("#", "");
        let rC = (hexColor >> 16) & 0xff;
        let gC = (hexColor >> 8) & 0xff;
        let bC = hexColor & 0xff;
        colorStops.push(
          "rgba(" +
            rC +
            "," +
            gC +
            "," +
            bC +
            "," +
            (colorstop.opacity ?? 0) +
            ")"
        );
      });
    }
  }

  const shapeProps = {
    element: props.element,
    listening: !attributes.hidden,
    shapeRef,
    gradientPoints: [x1, y1, x2, y2],
    renderProps: {
      name: `${props.containerId}-object`,
      id: props.element.id,
      locked: attributes.locked,
      key: props.element.id,
      x: attributes.x,
      y: attributes.y,
      width: attributes.width,
      height: attributes.height,
      rotation: attributes.rotation,
      cornerRadius:
        attributes.shapeType === "Rectangle" &&
        getCornerRadius(attributes.cornerRadius, node),
      opacity: attributes.opacity,
      visible: !attributes.hidden,
      scaleX: 1,
      scaleY: 1,
      draggable:
        !attributes.locked &&
        !props.isSelectDescribe &&
        !props.isMultipleSelection &&
        !props.isDragging &&
        props.editMode,
      fill: image ? undefined : attributes.backgroundColor,
      fillPatternImage: image,
      fillPriority:
        attributes.shapeType !== "Custom"
          ? attributes.bgGradient && attributes.bgGradient.hasGradient
            ? attributes.bgGradient.style === "linear"
              ? "linear-gradient"
              : "radial-gradient"
            : "color"
          : "color",
      fillLinearGradientStartPoint:
        attributes.shapeType !== "Custom" ? { x: x1, y: y1 } : undefined,
      fillLinearGradientEndPoint:
        attributes.shapeType !== "Custom" ? { x: x2, y: y2 } : undefined,
      fillRadialGradientStartPoint:
        attributes.shapeType !== "Custom"
          ? { x: offsetX, y: offsetY }
          : undefined,
      fillRadialGradientStartRadius:
        attributes.shapeType !== "Custom" ? 0 : undefined,
      fillRadialGradientEndPoint:
        attributes.shapeType !== "Custom"
          ? { x: offsetX, y: offsetY }
          : undefined,
      fillRadialGradientEndRadius:
        attributes.shapeType !== "Custom" ? ratio : undefined,
      fillLinearGradientColorStops:
        attributes.shapeType !== "Custom" ? colorStops : undefined,
      fillRadialGradientColorStops:
        attributes.shapeType !== "Custom" ? colorStops : undefined,

      globalCompositeOperation: getBlendMode(attributes.blendMode),
      stroke: getStroke(),
      strokeWidth:
        attributes.shapeType !== "Custom"
          ? attributes.borderWidth ?? 0
          : undefined,
      dash:
        attributes.shapeType !== "Custom" &&
        attributes.borderDash &&
        attributes.borderWhiteSpace
          ? [attributes.borderDash, attributes.borderWhiteSpace]
          : undefined,
      onMouseDown: (e: KonvaEventObject<MouseEvent>) => {
        if (e.evt.button !== 2) props.onSelect();
      },
      onMouseOver: (e: KonvaEventObject<MouseEvent>) => {
        const locked = props.isSelectorActive && attributes.locked;
        if (!locked) props.onOver(true);
      },
      onMouseOut: (e: KonvaEventObject<MouseEvent>) => {
        props.onOver(false);
      },
      onDragMove: (e: KonvaEventObject<DragEvent>) => {
        props.onDrag(shapeRef, trRef);
      },
      onDragStart: (e: KonvaEventObject<DragEvent>) => {
        if (e.evt.shiftKey) shapeRef.current?.stopDrag();
      },
      onDragEnd: (e: KonvaEventObject<DragEvent>) => {
        if (node) {
          let rotation = node.rotation();
          let resizeValues: IResizeValues = {
            x: node.x(),
            y: node.y(),
            width: node.width(),
            height: node.height(),
            rotation: rotation ? rotation : undefined,
          };
          props.onDragEnd(shapeRef, trRef, resizeValues);
        }
      },
      onTransform: (e: KonvaEventObject<Event>) => {
        if (node) {
          if (attributes.cornerRadius && attributes.shapeType === "Rectangle") {
            const shape = node as any;
            shape.cornerRadius(getCornerRadius(attributes.cornerRadius, node));
          }
          if (attributes.shapeType !== "Line") {
            node.width(node.width() * node.scaleX());
            node.height(node.height() * node.scaleY());
            node?.scaleX(1);
            node?.scaleY(1);
          }
          const activeAnchor = trRef.current.getActiveAnchor();
          let rotation = node.rotation();
          if (activeAnchor === "rotater") {
            let stageX = trRef.current.getStage()?.x();
            let stageY = trRef.current.getStage()?.y();
            let container = trRef.current
              .getStage()
              ?.container() as HTMLDivElement;
            let offsetX = container.offsetLeft + (stageX ?? 0);
            let offsetY = container.offsetTop + (stageY ?? 0);
            props.onRotation(
              rotation,
              node.x() - 15,
              node.y() - 15,
              offsetX,
              offsetY
            );
          }
        }
      },
      onTransformEnd: (e: KonvaEventObject<Event>) => {
        if (node) {
          const scaleX = node!.scaleX();
          const scaleY = node!.scaleY();
          node?.scaleX(1);
          node?.scaleY(1);
          let newWidth = Math.max(5, node!.width() * scaleX);
          let newHeight = Math.max(node!.height() * scaleY);
          let rotation = node.rotation();
          let resizeValues: IResizeValues = {
            x: node.x(),
            y: node.y(),
            width: newWidth,
            height: newHeight,
            rotation: rotation ? rotation : undefined,
          };
          props.onResize(resizeValues);
          props.onRotation();
        }
      },
    },
  };

  return (
    <>
      {attributes.shapeType === "Rectangle" && (
        <RectangleCanvasElement {...shapeProps} />
      )}
      {attributes.shapeType === "Circle" && (
        <CircleCanvasElement {...shapeProps} />
      )}
      {attributes.shapeType === "Ellipse" && (
        <EllipseCanvasElement {...shapeProps} />
      )}
      {attributes.shapeType === "Star" && <StarCanvasElement {...shapeProps} />}
      {attributes.shapeType === "Triangle" && (
        <TriangleCanvasElement {...shapeProps} />
      )}
      {attributes.shapeType === "Rhombus" && (
        <RhombusCanvasElement {...shapeProps} />
      )}
      {attributes.shapeType === "Line" && <LineCanvasElement {...shapeProps} />}
      {attributes.shapeType === "Custom" && (
        <CustomCanvasElement {...shapeProps} />
      )}
      {(props.isSelected || props.isOver || showDescribedColor) && (
        <Transformer
          ref={trRef}
          borderStroke={getTransformerBorderColor()}
          listening={!attributes.hidden}
          keepRatio={true}
          padding={attributes.shapeType === "Line" ? 10 : undefined}
          borderStrokeWidth={props.isOver || showDescribedColor ? 2 : 1}
          rotateEnabled={
            attributes.locked !== true &&
            props.isSelected &&
            !props.isMultipleSelection &&
            !props.isSelectDescribe
          }
          resizeEnabled={
            props.isSelected &&
            !props.isMultipleSelection &&
            !props.isSelectDescribe
          }
          rotateAnchorOffset={20}
          rotationSnaps={[0, 45, 90, 135, 180, 225, 270, 315]}
          enabledAnchors={
            attributes.locked
              ? []
              : attributes.shapeType === "Line"
              ? ["middle-left", "middle-right"]
              : attributes.shapeType === "Rectangle" ||
                attributes.shapeType === "Ellipse"
              ? undefined
              : ["top-left", "bottom-left", "top-right", "bottom-right"]
          }
          boundBoxFunc={(oldBox, newBox) => {
            if (
              attributes.shapeType !== "Line" &&
              (newBox.width < 5 || newBox.height < 5)
            ) {
              return oldBox;
            }
            return newBox;
          }}
        >
          <ElementLockedAddIn ref={lockIconRef} />
          <CornersAddIn
            shapeType={attributes.shapeType}
            show={props.isSelected && props.isSelectDescribe}
            width={shapeRef.current ? shapeRef.current.width() : 0}
            height={shapeRef.current ? shapeRef.current.height() : 0}
            borderWidth={attributes.borderWidth}
            scaleX={
              !shapeRef.current
                ? 1
                : shapeRef.current.getLayer()?.getStage().scaleX() ?? 1
            }
            scaleY={
              !shapeRef.current
                ? 1
                : shapeRef.current.getLayer()?.getStage().scaleY() ?? 1
            }
          />
        </Transformer>
      )}
    </>
  );
};

export default ShapeCanvasElement;
